import React, { useEffect, useId, useMemo, useRef, useState } from "react";

type Placement = "auto" | "left" | "right" | "center";

type Props = {
    text: React.ReactNode;
    className?: string;
    placement?: Placement;
    widthClassName?: string;
};

export const HelpTip: React.FC<Props> = ({
    text,
    className,
    placement = "auto",
    widthClassName,
}) => {
    const id = useId();

    const [open, setOpen] = useState(false);
    const [visible, setVisible] = useState(false);
    const [resolved, setResolved] = useState<Exclude<Placement, "auto">>("center");

    const wrapRef = useRef<HTMLSpanElement | null>(null); // ←追加
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const rafRef = useRef<number | null>(null);

    const posClass = useMemo(() => {
        if (resolved === "right") return "right-0";
        if (resolved === "left") return "left-0";
        return "left-1/2 -translate-x-1/2";
    }, [resolved]);

    useEffect(() => {
        if (!open) {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            setVisible(false);
            return;
        }

        setVisible(false);

        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
            setVisible(true);
            rafRef.current = null;
        });

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const decide = () => {
            if (placement !== "auto") {
                setResolved(placement === "center" ? "center" : placement);
                return;
            }
            const el = btnRef.current;
            if (!el) return;

            const r = el.getBoundingClientRect();
            const vw = window.innerWidth;

            if (r.left < 160) setResolved("left");
            else if (vw - r.right < 160) setResolved("right");
            else setResolved("center");
        };

        decide();
        window.addEventListener("resize", decide);
        window.addEventListener("scroll", decide, true);
        return () => {
            window.removeEventListener("resize", decide);
            window.removeEventListener("scroll", decide, true);
        };
    }, [open, placement]);

    // ←追加: 外側クリックで閉じる
    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (e: MouseEvent) => {
            const el = wrapRef.current;
            if (!el) return;
            if (!el.contains(e.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
        };
    }, [open]);

    return (
        <span
            ref={wrapRef}
            className={`relative inline-flex items-center ${className ?? ""}`}
        >
            <button
                ref={btnRef}
                type="button"
                aria-label="ヘルプ"
                aria-describedby={id}
                className="pointer-events-auto inline-flex h-5 w-5 items-center justify-center rounded-full border bg-white text-[11px] leading-none text-slate-600 hover:bg-slate-50"
                onMouseDown={(e) => {
                    e.stopPropagation();
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
            >
                ?
            </button>

            <span
                id={id}
                role="tooltip"
                className={[
                    "absolute top-full z-50 mt-2",
                    posClass,
                    widthClassName ?? "w-72 max-w-[90vw]",
                    "rounded-md border bg-white px-3 py-2 text-xs text-slate-700 shadow-lg",
                    visible ? "block" : "hidden",
                ].join(" ")}
            >
                {text}
            </span>
        </span>
    );
};